import { Button, Modal } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DocumentUploadList from './DocumentUploadList';
import { ProcessingScreen } from 'components/WorkOrder/WOScanProcessingScreen';
import { sortFilesByValidity } from 'shared/utils';
import * as Sentry from '@sentry/react';

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 30px;
    color: #02103d;
  }

  .ant-modal-title {
    color: #02103d;
  }

  .ant-modal-close-x {
    color: #02103d;
  }

  .loading-box {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 5px;
    padding: 0.4rem;
    width: 100%;
    span {
      font-size: 14px;
      color: #02103d;
    }
  }
`;

const StyledP = styled.p`
  font-size: 14px;
`;

const UploadImageModal = ({
  isVisible,
  onUpload,
  onCancel,
  isLoading,
  isError,
  uploading,
  // progress,
  currentStep,
  initialFile,
}) => {
  const [fileList, setFileList] = useState([]);
  const [currentLoadingMsg, setCurrentLoadingMsg] = useState(
    'Hang tight, we are processing your files...'
  );

  const intervalRef = useRef(null);

  const randomLoadingMessages = [
    'Hang tight, we are processing your files...',
    'Working on your files... almost there!',
  ];

  useEffect(() => {
    if (isVisible) {
      intervalRef.current = setInterval(() => {
        setCurrentLoadingMsg(
          randomLoadingMessages[
            Math.floor(Math.random() * randomLoadingMessages.length)
          ]
        );
      }, 3000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isVisible, randomLoadingMessages]);

  useEffect(() => {
    if (isVisible) {
      setFileList(initialFile || []);
    }
  }, [isVisible]);

  const handleFileChange = ({ fileList: newFileList }) => {
    // Filter out any files that exceed the size limit
    const { validFiles, oversizedFiles } = sortFilesByValidity(newFileList);

    // If any files were filtered out, show error for the first one
    if (oversizedFiles.length > 0) {
      const oversizedFile = oversizedFiles[0];
      if (oversizedFile) {
        Sentry.captureException(new Error('Files Too Large'), {
          extra: {
            oversizedFile,
          },
        });
        Modal.error({
          title: 'File Too Large',
          content: `"${oversizedFile.name}" is ${(oversizedFile.size / (1024 * 1024)).toFixed(1)}MB. Maximum file size is 12MB.`,
        });
      }
    }

    setFileList(validFiles);
  };

  const uploadProps = {
    accept: '.png,.jpg,.jpeg,.pdf',
    onRemove: (file) => {
      setFileList((curr) => {
        const index = curr.indexOf(file);
        const newFileList = curr.slice();
        newFileList.splice(index, 1);
        return newFileList;
      });
    },
    beforeUpload: (file) => {
      setFileList((curr) => [...curr, file]);
      return false; // Prevent automatic uploading
    },
    fileList,
    onChange: handleFileChange,
    multiple: false,
  };

  return (
    <StyledModal
      title="AI-Powered Work Order Scanner"
      open={isVisible}
      onOk={() => onUpload(fileList)}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          loading={isLoading}
          disabled={fileList.length === 0}
          key="submit"
          type="primary"
          onClick={() => onUpload(fileList)}
        >
          {isLoading ? 'Processing' : 'Process'}
        </Button>,
      ]}
    >
      <StyledP style={{ marginTop: -10 }}>
        Pitstop’s AI converts each file into structured maintenance data. Follow
        these guidelines for best results:
      </StyledP>
      <ul>
        {/* <li>Only English is supported.</li> */}
        <li>Supported formats are PNG, JPG, PDF (&lt; 12MB).</li>
        <li>Each work order must be a separate file.</li>
        <li>
          Clear, high‑res (&gt;250dpi) images work best; avoid shadows, skewed
          pages or large watermarks.
        </li>
        {/* <li>Handwritten Notes are accepted but not officially supported.</li> */}
        {/* <br />
        <li>Each work order will be associated with 1 line item.</li>
        <li>Please scan work orders in the same sequence as the line items. New
          line items will automatically be created as necessary.</li> */}
      </ul>
      <StyledP style={{ marginBottom: 0 }}>
        <b>Add Work Orders:</b>
      </StyledP>
      <DocumentUploadList
        isDisabled={fileList.length > 0}
        uploadProps={uploadProps}
      />
      {uploading && (
        <div className="loading-box">
          {/* <Progress percent={progress} /> */}
          <ProcessingScreen currentStep={currentStep} />
        </div>
      )}
      {/* {isLoading && (
        <div className="loading-box">
          <span>{currentLoadingMsg}</span>
        </div>
      )} */}
      {isError && (
        <div className="loading-box">
          <span
            style={{
              color: 'red',
            }}
          >
            Something went wrong. Please try again later.
          </span>
        </div>
      )}
    </StyledModal>
  );
};

UploadImageModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  uploading: PropTypes.bool,
  // progress: PropTypes.number,
  currentStep: PropTypes.number,
  initialFile: PropTypes.array,
};

UploadImageModal.defaultProps = {
  isLoading: false,
  isError: false,
  uploading: false,
  // progress: 0,
  currentStep: 0,
};

export default UploadImageModal;
